import React from 'react';
import { CenteredSpinner, ComingSoonHero } from '@components/ui/feedback';
import { PageContainer, PageHeader } from '@components/ui/layout';
import { useSubjectLanguageVariants } from '@features/user';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { useEventTracking } from '@services/statsig';

import { useStripeConnect } from '../store/hooks';

import { RequestPaymentButton } from './RequestPaymentButton';
import { StripeInvoicesTable } from './StripeInvoicesTable';

/**
 * BillPatientsPage Component
 *
 * A page for managing patient billing and invoices.
 * Shows Stripe invoices table when connected, or a "coming soon" style
 * page with Stripe Connect setup when not connected.
 */
export const BillPatientsPage: React.FC = () => {
  const { hasStripeAccount, startOnboarding, isCreating, isLoading } = useStripeConnect();
  const { payment, stripeConnect } = useEventTracking();
  const { pluralCap: subjectLanguagePluralCap, plural: subjectLanguagePlural } =
    useSubjectLanguageVariants();

  const handleGetStartedClick = async () => {
    // Track the click
    payment.billPatientsGetStartedClicked();

    // Track Stripe Connect initiation from this page
    stripeConnect.initiated({ source: 'bill_patients_page' });

    try {
      const result = await startOnboarding();
      // Redirect to Stripe onboarding
      window.location.href = result.onboarding_url;
    } catch (error) {
      // Track failure
      stripeConnect.failed({
        error_type: 'onboarding_start_error',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  // Show loading spinner while checking Stripe connection status
  if (isLoading) {
    return <CenteredSpinner />;
  }

  return (
    <PageContainer>
      <PageHeader
        title={hasStripeAccount ? `Bill Your ${subjectLanguagePluralCap}` : ' '}
        action={
          <RequestPaymentButton
            label="Request payment"
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
            }}
          />
        }
      />
      {hasStripeAccount ? (
        <StripeInvoicesTable />
      ) : (
        <ComingSoonHero
          title={`Bill Your ${subjectLanguagePluralCap}`}
          description={`Bill your ${subjectLanguagePlural} securely — right from JotPsych.`}
          primaryAction={{
            label: isCreating ? 'Connecting...' : 'Get started',
            startIcon: <ArrowForwardIcon />,
            onClick: () => void handleGetStartedClick(),
            variant: 'contained',
          }}
          useAvatar={false}
        />
      )}
    </PageContainer>
  );
};
